import { useEffect, useMemo, useState } from "react"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"

import {
  Badge,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@f2e/ui/components/base"

import { EmptyDataPanel, LoadingPage } from "@/components/System"
import { PageNav } from "@/components"
import { useStore } from "@/store"
import {
  agentResponseToAgent,
  FetchStatus,
  type WorkspacePaymentRequestListFilters,
  type WorkspacePaymentRequestListItemResponse,
  useGetAgentsApi,
  useListWorkspacePaymentRequestsApi,
} from "@/hooks/api"
import { Agent, Workspace } from "@/types"

export interface WorkspacePaymentRequestListProps {
  workspace: Workspace
  filters: WorkspacePaymentRequestListFilters
  page: number
  onPageChange: (page: number) => void
  onFiltersChange: (
    nextFilters: Partial<WorkspacePaymentRequestListFilters>
  ) => void
  onSelectPaymentRequest: (paymentRequestId: string) => void
}

const PAYMENT_REQUESTS_PAGE_LIMIT = 20

const formatDateTime = (value?: string | null) => {
  if (!value) return "—"
  return format(new Date(value), "yyyy-MM-dd HH:mm")
}

const getStatusBadgeClassName = (status: string) => {
  switch (status) {
    case "succeeded":
      return "bg-green-600 text-white hover:bg-green-700 border-transparent"
    case "pending":
      return "bg-slate-900 text-white hover:bg-slate-800 border-transparent"
    case "processing":
      return "bg-amber-500 text-white hover:bg-amber-600 border-transparent"
    case "failed":
      return "bg-rose-600 text-white hover:bg-rose-700 border-transparent"
    case "refunded":
      return "bg-violet-600 text-white hover:bg-violet-700 border-transparent"
    case "cancelled":
    case "expired":
      return "bg-slate-200 text-slate-700 hover:bg-slate-200 border-transparent dark:bg-slate-800 dark:text-slate-200"
    default:
      return ""
  }
}

const renderCell = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "—"
  return String(value)
}

const WorkspacePaymentRequestList = ({
  workspace,
  filters,
  page,
  onPageChange,
  onFiltersChange,
  onSelectPaymentRequest,
}: WorkspacePaymentRequestListProps) => {
  const { t } = useTranslation()
  const userMe = useStore((state) => state.userMe)
  const organizationId =
    workspace.organizationId ?? userMe?.profile?.defaultOrganizationId ?? ""

  const [agents, setAgents] = useState<Agent[]>([])
  const [rows, setRows] = useState<WorkspacePaymentRequestListItemResponse[]>(
    []
  )
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  const {
    data: agentsResp,
    status: agentsStatus,
    fetchFn: fetchAgents,
  } = useGetAgentsApi()
  const {
    data: listResp,
    error: listError,
    fetchFn: fetchPaymentRequests,
    status: listStatus,
  } = useListWorkspacePaymentRequestsApi(workspace.id)

  useEffect(() => {
    if (!organizationId) return

    fetchAgents({
      searchParams: new URLSearchParams({
        oid: organizationId,
        wid: workspace.id,
      }),
    }).catch((error) => {
      console.error("Failed to fetch payment request agents", error)
    })
  }, [fetchAgents, organizationId, workspace.id])

  useEffect(() => {
    if (!agentsResp?.data || agentsStatus !== FetchStatus.Success) return

    const mappedAgents = agentsResp.data
      .map((resp) => agentResponseToAgent(resp))
      .filter((agent) => agent.workspaceId === workspace.id)

    setAgents(mappedAgents)
  }, [agentsResp, agentsStatus, workspace.id])

  useEffect(() => {
    const searchParams = new URLSearchParams({
      limit: PAYMENT_REQUESTS_PAGE_LIMIT.toString(),
      offset: ((page - 1) * PAYMENT_REQUESTS_PAGE_LIMIT).toString(),
      order_by: "-created_at",
    })

    if (filters.agent_id && filters.agent_id !== "all") {
      searchParams.set("agent_id", filters.agent_id)
    }
    if (filters.created_from) {
      searchParams.set("created_from", filters.created_from)
    }
    if (filters.created_to) {
      searchParams.set("created_to", filters.created_to)
    }
    if (filters.query?.trim()) {
      searchParams.set("query", filters.query.trim())
    }
    if (filters.include_guest_requests === false) {
      searchParams.set("include_guest_requests", "false")
    }

    fetchPaymentRequests({ searchParams }).catch(() => {
      // errors are surfaced by the hook state
    })
  }, [fetchPaymentRequests, filters, page])

  useEffect(() => {
    setRows(listResp?.data || [])
    setTotalPages(listResp?.pagination?.total_pages || 1)
    setTotalRecords(listResp?.pagination?.total_records || 0)
  }, [listResp])

  const agentOptions = useMemo(
    () => [
      {
        value: "all",
        label: t("workspace.payments.requestList.filters.allAgents"),
      },
      ...agents.map((agent) => ({
        value: agent.id,
        label: agent.name || agent.id,
      })),
    ],
    [agents, t]
  )

  const selectedAgentLabel = useMemo(() => {
    if (!filters.agent_id || filters.agent_id === "all") {
      return t("workspace.payments.requestList.filters.allAgents")
    }

    return (
      agentOptions.find((option) => option.value === filters.agent_id)?.label ??
      t("workspace.payments.requestList.filters.allAgents")
    )
  }, [agentOptions, filters.agent_id, t])

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end gap-4 rounded-xl border bg-background p-4">
        <div className="min-w-[260px] flex-1">
          <Label className="mb-2 block text-xs text-muted-foreground">
            {t("workspace.payments.requestList.filters.search")}
          </Label>
          <Input
            value={filters.query || ""}
            onChange={(event) => onFiltersChange({ query: event.target.value })}
            placeholder={t(
              "workspace.payments.requestList.filters.searchPlaceholder"
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-xs text-muted-foreground">
            {t("workspace.payments.requestList.filters.agent")}
          </Label>
          <Select
            value={filters.agent_id || "all"}
            onValueChange={(value) => onFiltersChange({ agent_id: value })}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder={selectedAgentLabel} />
            </SelectTrigger>
            <SelectContent>
              {agentOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-xs text-muted-foreground">
            {t("workspace.payments.requestList.filters.createdFrom")}
          </Label>
          <Input
            type="date"
            className="w-40"
            value={filters.created_from || ""}
            onChange={(event) =>
              onFiltersChange({ created_from: event.target.value })
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-xs text-muted-foreground">
            {t("workspace.payments.requestList.filters.createdTo")}
          </Label>
          <Input
            type="date"
            className="w-40"
            value={filters.created_to || ""}
            onChange={(event) =>
              onFiltersChange({ created_to: event.target.value })
            }
          />
        </div>

        <label className="flex min-h-10 items-center gap-3 rounded-lg border border-dashed px-3 py-2 text-sm text-foreground">
          <Checkbox
            checked={filters.include_guest_requests !== false}
            onCheckedChange={(checked) =>
              onFiltersChange({ include_guest_requests: checked !== false })
            }
          />
          <span>
            {t("workspace.payments.requestList.filters.includeGuestRequests")}
          </span>
        </label>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-2">
          <h4 className="text-xl font-semibold tracking-tight">
            {t("workspace.payments.requestList.title")}
          </h4>
          <span className="text-sm text-muted-foreground">
            {t("workspace.payments.requestList.totalRecords", {
              count: totalRecords,
            })}
          </span>
        </div>
        <div className="text-sm text-muted-foreground md:text-right">
          {t("workspace.payments.requestList.summaryHint")}
        </div>
      </div>

      {listStatus === FetchStatus.Loading && rows.length === 0 ? (
        <LoadingPage message={t("workspace.payments.requestList.loading")} />
      ) : null}

      {listError ? (
        <EmptyDataPanel
          message={t("workspace.payments.requestList.errors.loadFailed")}
        />
      ) : null}

      {!listError && listStatus !== FetchStatus.Loading ? (
        <>
          {rows.length === 0 ? (
            <EmptyDataPanel
              message={t("workspace.payments.requestList.empty")}
            />
          ) : (
            <>
              <div className="overflow-hidden rounded-xl border bg-background">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableHead>
                        {t(
                          "workspace.payments.requestList.table.requestNumber"
                        )}
                      </TableHead>
                      <TableHead>
                        {t("workspace.payments.requestList.table.title")}
                      </TableHead>
                      <TableHead>
                        {t("workspace.payments.requestList.table.amount")}
                      </TableHead>
                      <TableHead>
                        {t("workspace.payments.requestList.table.status")}
                      </TableHead>
                      <TableHead>
                        {t(
                          "workspace.payments.requestList.table.externalUserId"
                        )}
                      </TableHead>
                      <TableHead>
                        {t("workspace.payments.requestList.table.agent")}
                      </TableHead>
                      <TableHead>
                        {t("workspace.payments.requestList.table.createdAt")}
                      </TableHead>
                      <TableHead>
                        {t("workspace.payments.requestList.table.paidAt")}
                      </TableHead>
                      <TableHead>
                        {t("workspace.payments.requestList.table.updatedAt")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow
                        key={row.payment_request_id}
                        className={`cursor-pointer hover:bg-muted/50 ${
                          index % 2 === 0 ? "bg-background" : "bg-muted/30"
                        }`}
                        onClick={() =>
                          onSelectPaymentRequest(row.payment_request_id)
                        }
                      >
                        <TableCell className="font-medium">
                          {row.merchant_order_no}
                        </TableCell>
                        <TableCell>{renderCell(row.item_desc)}</TableCell>
                        <TableCell className="font-medium tabular-nums">
                          {row.amount_twd}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={getStatusBadgeClassName(row.status)}
                          >
                            {t(
                              `workspace.payments.requestList.statuses.${row.status}`,
                              row.status
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {renderCell(row.external_user_id)}
                        </TableCell>
                        <TableCell>{renderCell(row.agent_name)}</TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {formatDateTime(row.created_at)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {formatDateTime(row.paid_at)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {formatDateTime(row.updated_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 ? (
                <div className="flex justify-center">
                  <PageNav
                    page={page}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                  />
                </div>
              ) : null}
            </>
          )}
        </>
      ) : null}
    </section>
  )
}

export default WorkspacePaymentRequestList
